import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePageContext } from '../contexts/PageContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase-config';
import { signOutAndRedirectToLogin } from '../utils/logoutFlow';
import { doc, getDoc } from 'firebase/firestore';
import EmailVerificationBanner from './EmailVerificationBanner';
import QuestionnaireCompletionBanner from './QuestionnaireCompletionBanner';
import { Sun, Moon, Bell, ChevronDown, UserCircle, LifeBuoy, Zap, LogOut, Headphones, Search, BookOpen, Info } from 'lucide-react';
import {
  getAccountPlanLabel,
  getBadgeFromUserData,
  shouldShowUpgradeAction,
} from '../config/userRoleBadges';
import { useOrcaEngine4Pane } from '../config/orcaEngineFlags';
import { useRuntimeBuildStatus } from '../hooks/useRuntimeBuildStatus';

// Single product BRANDING - ADU Lab
const BRANDING = {
  name: 'ADU Lab',
  shortName: 'LAB',
  tagline: 'Your strategic ADU guide',
  primaryColor: '#8b5cf6', // violet
  logo: 'https://storage.googleapis.com/adu-lab-style-images/44Labs_IconLogo.png',
};

const AppShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userStatus, userRoles, userEntitlements, userAccount } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  usePageContext(); // Keep context mounted
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userFullName, setUserFullName] = useState('');
  const [isDynamic, setIsDynamic] = useState(false);
  const [menuCloseTimeout, setMenuCloseTimeout] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const buildStatus = useRuntimeBuildStatus(showUserMenu);
  const [showLabInterface, setShowLabInterface] = useState(
    () => localStorage.getItem('showLabInterface') === 'true'
  );

  // Initialize notifications - show default ones if not dismissed
  useEffect(() => {
    const dismissedIds = JSON.parse(localStorage.getItem('dismissedNotifications') || '[]');
    const defaultNotifications = [
      {
        id: 'welcome-notification',
        type: 'info',
        icon: 'celebration',
        title: 'Welcome to ADU Lab!',
        message: 'We\'re excited to have you here. Explore our tools and resources to plan your ADU project with confidence.',
        timestamp: new Date().toISOString(),
        actionLabel: 'Get Started',
        actionRoute: '/dashboard',
      },
      {
        id: 'complete-questionnaire',
        type: 'action',
        icon: 'assignment',
        title: 'Complete Your Project Questionnaire',
        message: 'Answer a few questions about your property and goals to unlock personalized insights and recommendations.',
        timestamp: new Date().toISOString(),
        actionLabel: 'Start Questionnaire',
        actionRoute: '/dashboard',
      },
    ];

    const activeNotifications = defaultNotifications.filter(n => !dismissedIds.includes(n.id));
    setNotifications(activeNotifications);
  }, []);

  // Dismiss a notification
  const dismissNotification = (notificationId) => {
    const dismissedIds = JSON.parse(localStorage.getItem('dismissedNotifications') || '[]');
    if (!dismissedIds.includes(notificationId)) {
      dismissedIds.push(notificationId);
      localStorage.setItem('dismissedNotifications', JSON.stringify(dismissedIds));
    }
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Listen for localStorage changes (from settings toggle)
  useEffect(() => {
    const handleStorageChange = () => {
      setShowLabInterface(localStorage.getItem('showLabInterface') === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Define navigation items - conditionally show Lab Interface tabs
  // Recalculates when currentUser or showLabInterface changes
  const navItems = React.useMemo(() => {
    if (currentUser) {
      const baseItems = [];

      // Only show Dash, Hub, Lens if Lab Interface is enabled
      if (showLabInterface) {
        baseItems.push(
          { path: '/dash', label: 'Dash', icon: 'edit' },
          { path: '/hub', label: 'Hub', icon: 'hub' },
          { path: '/lens', label: 'Lens', icon: 'visibility' }
        );
      }

      return baseItems;
    }

    return [];
  }, [currentUser, showLabInterface]);

  // Fetch user's full name and isDynamic from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserFullName(userData.fullName || '');
            setIsDynamic(userData.isDynamic || false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleLogout = async () => {
    await signOutAndRedirectToLogin({ auth, signOutFn: signOut });
  };

  // Remove the global click handler entirely - it's causing navigation issues

  // Close dropdowns when route changes
  useEffect(() => {
    setShowUserMenu(false);

    // Clear any pending timeouts
    if (menuCloseTimeout) {
      clearTimeout(menuCloseTimeout);
      setMenuCloseTimeout(null);
    }
  }, [location.pathname]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (menuCloseTimeout) clearTimeout(menuCloseTimeout);
    };
  }, [menuCloseTimeout]);

  // Get user's display name or email
  const getUserDisplayName = () => {
    if (userFullName) return userFullName;
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.email) return currentUser.email.split('@')[0];
    return 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    if (name.includes(' ')) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get badge configuration from centralized config
  const roleBadge = getBadgeFromUserData(userStatus, userRoles, userEntitlements, isDynamic, userAccount);
  const accountPlanLabel = getAccountPlanLabel(userStatus, userRoles, userEntitlements, isDynamic, userAccount);
  const showUpgradeAction = shouldShowUpgradeAction(userStatus, userRoles, userEntitlements, isDynamic, userAccount);

  // Orca Engine 4-Pane Layout - bypass AppShell completely
  const isOrcaEngine4Pane = useOrcaEngine4Pane();
  const isDashRoute = ['/dash', '/hub', '/lens', '/lab'].includes(location.pathname);

  useEffect(() => {
    document.title = 'ADU Lab';
  }, [location.pathname]);

  if (isOrcaEngine4Pane && isDashRoute) {
    // Return children directly without AppShell wrapper
    return <>{children}</>;
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-[#f8f9fa] dark:bg-[#0a0a0a]">
      <EmailVerificationBanner />
      <QuestionnaireCompletionBanner />
      {/* Header */}
      <header className="bg-white dark:bg-[#111827] border-b border-[#e0e0e0] dark:border-[#374151] z-40 shrink-0 relative">
        <div className="px-6 h-16 flex items-center justify-between">
          {/* Logo and Nav */}
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2 sm:space-x-3 relative">
              {/* Mobile Logo - Links to /dash with reset */}
              <Link
                to={`/dash?reset=${Date.now()}`}
                className="md:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Go to Dashboard"
              >
                <img
                  src={BRANDING.logo}
                  alt={`${BRANDING.name} Logo`}
                  className="h-8 w-auto"
                />
              </Link>

              {/* Desktop Logo - Links to /dash with reset */}
              <div className="hidden md:flex items-center space-x-2 sm:space-x-3">
                <Link to={`/dash?reset=${Date.now()}`}>
                  <img
                    src={BRANDING.logo}
                    alt={`${BRANDING.name} Logo`}
                    className="h-8 w-auto"
                  />
                </Link>
                <Link to={`/dash?reset=${Date.now()}`} className="hidden sm:block">
                  <h1 className="text-lg font-light tracking-[0.3em] text-[#313131]/95 dark:text-white/90 font-google-sans whitespace-nowrap antialiased hover:opacity-80 transition-opacity">
                    ADU&nbsp;<span className="inline-block h-px align-middle mx-1 dark:bg-white/80" style={{ width: '24px', backgroundColor: BRANDING.primaryColor }}></span>&nbsp;{BRANDING.shortName}
                  </h1>
                </Link>
              </div>

            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2">
              {navItems.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3 py-2 text-[16px] font-google-sans font-medium rounded-md transition-all antialiased ${isActive
                      ? 'text-white bg-[#1a73e8] shadow-sm'
                      : 'text-slate-600 dark:text-[#dee4ee] hover:bg-[#f1f3f4] dark:hover:bg-[#313d4a]'
                      }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* User Menu */}
          <div className="flex items-center">
            <div className="relative">
              <button
                className="flex items-center space-x-1.5 pl-1.5 pr-1.5 py-1 border border-[#e0e0e0] dark:border-[#2e3a47] rounded-full hover:bg-[#f1f3f4] dark:hover:bg-[#313d4a] cursor-pointer"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: '#1a73e8' }}
                >
                  {getUserInitials()}
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 dark:text-[#aeb7c0]" />
              </button>

              {showUserMenu && (
                <div
                  className="fixed sm:absolute top-16 sm:top-full right-4 sm:right-0 mt-1 pt-1 left-4 sm:left-auto w-auto sm:w-72 max-w-[calc(100vw-2rem)] sm:max-w-none z-50"
                  onMouseEnter={() => {
                    if (menuCloseTimeout) {
                      clearTimeout(menuCloseTimeout);
                      setMenuCloseTimeout(null);
                    }
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setShowUserMenu(false);
                    }, 100);
                    setMenuCloseTimeout(timeout);
                  }}
                >
                  <div className="bg-white dark:bg-[#111827] border-2 border-[#e0e0e0] dark:border-[#374151] rounded-xl shadow-xl">
                    <div className="p-4 bg-gradient-to-r from-[#f8f9fa] to-[#f1f3f4] dark:from-[#1f2937] dark:to-[#111827] rounded-t-xl border-b border-[#e0e0e0] dark:border-[#374151]">
                      <div className="flex items-start space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                          style={{ backgroundColor: '#1a73e8', boxShadow: '0 0 0 4px rgba(26, 115, 232, 0.15)' }}
                        >
                          {getUserInitials()}
                        </div>
                        <div className="flex-1">
                          <p className="font-google-sans font-medium text-base text-[#1a73e8] dark:text-blue-500">{getUserDisplayName()}</p>
                          <p className="text-sm text-[#5f6368] dark:text-gray-400 mt-1" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                            {accountPlanLabel || roleBadge?.description?.split(' - ')[0] || 'Free User'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      {currentUser?.email && (
                        <div className="px-3 py-2 mb-1">
                          <p className="text-xs text-[#5f6368] dark:text-gray-400 truncate" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                            {currentUser.email}
                          </p>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          navigate('/account-settings');
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-[#313131] dark:text-gray-100 hover:bg-[#1a73e8]/5 dark:hover:bg-[#374151] rounded-md transition-colors"
                      >
                        <UserCircle className="w-5 h-5 sm:w-4 sm:h-4 mr-3 text-[#1a73e8] dark:text-blue-500" />
                        Account Settings
                      </button>
                      <button
                        onClick={() => {
                          navigate('/help');
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-[#313131] dark:text-gray-100 hover:bg-[#1a73e8]/5 dark:hover:bg-[#374151] rounded-md transition-colors"
                      >
                        <LifeBuoy className="w-5 h-5 sm:w-4 sm:h-4 mr-3 text-[#1a73e8] dark:text-blue-500" />
                        Help Center
                      </button>
                      <button
                        onClick={() => {
                          navigate('/support');
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-[#313131] dark:text-gray-100 hover:bg-[#1a73e8]/5 dark:hover:bg-[#374151] rounded-md transition-colors"
                      >
                        <Headphones className="w-5 h-5 sm:w-4 sm:h-4 mr-3 text-[#1a73e8] dark:text-blue-500" />
                        Support Team
                      </button>
                      <button
                        onClick={() => {
                          navigate('/learn');
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-[#313131] dark:text-gray-100 hover:bg-[#1a73e8]/5 dark:hover:bg-[#374151] rounded-md transition-colors"
                      >
                        <BookOpen className="w-5 h-5 sm:w-4 sm:h-4 mr-3 text-[#1a73e8] dark:text-blue-500" />
                        Learning Guides
                      </button>
                      {showUpgradeAction && (
                        <button
                          onClick={() => {
                            navigate('/account-settings?tab=subscription');
                            setShowUserMenu(false);
                          }}
                          className="flex items-center w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-[#313131] dark:text-white hover:bg-[#1a73e8]/5 dark:hover:bg-[#4285f4]/10 rounded-md transition-colors group"
                        >
                          <Zap className="w-5 h-5 sm:w-4 sm:h-4 mr-3 text-[#1a73e8] dark:text-[#4285f4] group-hover:text-[#fbbc04]" />
                          <span className="font-medium text-[#1a73e8] dark:text-[#4285f4]">Upgrade</span>
                        </button>
                      )}
                    </div>
                    <div className="p-2 border-t border-[#e0e0e0] dark:border-[#2e3a47]">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-[#dc3545] dark:text-[#f28b82] hover:bg-[#dc3545]/10 dark:hover:bg-[#f28b82]/10 rounded-md transition-colors"
                      >
                        <LogOut className="w-5 h-5 sm:w-4 sm:h-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                    {/* Quick Actions - Search, Theme, Notifications, Info */}
                    <div className="px-4 py-3 border-t border-[#e0e0e0] dark:border-[#2e3a47] flex items-center justify-center gap-4">
                      <button
                        onClick={() => {
                          setShowSearchModal(true);
                          setSearchQuery('');
                          setShowUserMenu(false);
                        }}
                        className="p-2 rounded-full hover:bg-[#f1f3f4] dark:hover:bg-[#313d4a] text-slate-500 dark:text-[#aeb7c0] transition-colors"
                        aria-label="Search"
                      >
                        <Search className="w-5 h-5" />
                      </button>
                      <button
                        onClick={toggleDarkMode}
                        className="p-2 rounded-full hover:bg-[#f1f3f4] dark:hover:bg-[#313d4a] text-slate-500 dark:text-[#aeb7c0] transition-colors"
                        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                      >
                        {darkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => {
                          setShowNotificationsModal(true);
                          setShowUserMenu(false);
                        }}
                        className="relative p-2 rounded-full hover:bg-[#f1f3f4] dark:hover:bg-[#313d4a] text-slate-500 dark:text-[#aeb7c0] transition-colors"
                        aria-label="Notifications"
                      >
                        <Bell className="w-5 h-5" />
                        {notifications.length > 0 && (
                          <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-[#1a73e8] ring-2 ring-white dark:ring-[#111827]"></span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('openProjectInfoModal'));
                          setShowUserMenu(false);
                        }}
                        className="p-2 rounded-full hover:bg-[#f1f3f4] dark:hover:bg-[#313d4a] text-slate-500 dark:text-[#aeb7c0] transition-colors"
                        aria-label="Project & Account Info"
                      >
                        <Info className="w-5 h-5" />
                      </button>
                    </div>
                    {/* Runtime status */}
                    <div className="px-4 py-2 bg-[#f8f9fa] dark:bg-[#0d1117] rounded-b-xl border-t border-[#e0e0e0] dark:border-[#2e3a47]">
                      <p className="text-[11px] text-[#9ca3af] dark:text-[#6b7280] text-center font-mono">
                        {buildStatus.label}: {buildStatus.commitShort} on {buildStatus.branch}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Global Search Modal */}
      {showSearchModal && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-20 sm:pt-32 px-4"
          onClick={() => setShowSearchModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal Content */}
          <div
            className="relative bg-white dark:bg-[#111827] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input */}
            <div className="relative border-b border-gray-200 dark:border-gray-700">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                autoFocus
                placeholder={`Search ${BRANDING.name}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    navigate(`/help?q=${encodeURIComponent(searchQuery.trim())}`);
                    setShowSearchModal(false);
                  }
                  if (e.key === 'Escape') {
                    setShowSearchModal(false);
                  }
                }}
                className="w-full pl-14 pr-12 py-5 text-lg bg-transparent text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
                style={{ fontFamily: "'Google Sans', sans-serif" }}
              />
              <button
                onClick={() => setShowSearchModal(false)}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Quick Links */}
            <div className="p-4">
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3 px-2" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                Quick Search In
              </p>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    navigate(searchQuery.trim() ? `/learn?q=${encodeURIComponent(searchQuery.trim())}` : '/learn');
                    setShowSearchModal(false);
                  }}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <span className="material-icons-outlined text-[#1a73e8] dark:text-blue-400 mr-3">school</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                      Learning Center
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Guides, glossary, workbooks & quizzes</p>
                  </div>
                  <span className="material-icons text-gray-400 group-hover:text-[#1a73e8] transition-colors">arrow_forward</span>
                </button>

                <button
                  onClick={() => {
                    navigate(searchQuery.trim() ? `/help?q=${encodeURIComponent(searchQuery.trim())}` : '/help');
                    setShowSearchModal(false);
                  }}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <span className="material-icons-outlined text-[#1a73e8] dark:text-blue-400 mr-3">help_outline</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                      Help Center
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">FAQs, tutorials & getting started</p>
                  </div>
                  <span className="material-icons text-gray-400 group-hover:text-[#1a73e8] transition-colors">arrow_forward</span>
                </button>

                <button
                  onClick={() => {
                    navigate(searchQuery.trim() ? `/support?q=${encodeURIComponent(searchQuery.trim())}` : '/support');
                    setShowSearchModal(false);
                  }}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <span className="material-icons-outlined text-[#1a73e8] dark:text-blue-400 mr-3">headset_mic</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                      Support Center
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Contact support & live chat</p>
                  </div>
                  <span className="material-icons text-gray-400 group-hover:text-[#1a73e8] transition-colors">arrow_forward</span>
                </button>

                <button
                  onClick={() => {
                    navigate(searchQuery.trim() ? `/help/platform-faqs?q=${encodeURIComponent(searchQuery.trim())}` : '/help/platform-faqs');
                    setShowSearchModal(false);
                  }}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <span className="material-icons-outlined text-[#1a73e8] dark:text-blue-400 mr-3">quiz</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                      Platform FAQs
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Frequently asked questions</p>
                  </div>
                  <span className="material-icons text-gray-400 group-hover:text-[#1a73e8] transition-colors">arrow_forward</span>
                </button>
              </div>
            </div>

            {/* Footer hint */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                Press <kbd className="px-1.5 py-0.5 mx-1 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Enter</kbd> to search in Help Center or <kbd className="px-1.5 py-0.5 mx-1 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Esc</kbd> to close
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowNotificationsModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal Content */}
          <div
            className="relative bg-white dark:bg-[#1f2937] rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e0e0e0] dark:border-gray-700 bg-gradient-to-r from-[#f8f9fa] to-[#f1f3f4] dark:from-[#313d4a] dark:to-[#2e3a47]">
              <div className="flex items-center gap-2">
                <span className="material-icons" style={{ fontSize: '20px', color: '#1a73e8' }}>add_comment</span>
                <h2 className="text-lg font-medium text-[#202124] dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                  Notifications
                </h2>
                {notifications.length > 0 && (
                  <span className="bg-[#1a73e8] text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    {notifications.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowNotificationsModal(false)}
                className="p-2 rounded-lg hover:bg-[#e5e7eb] dark:hover:bg-gray-600 text-[#5f6368] dark:text-gray-400"
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>

            {/* Content */}
            <div className="max-h-[60vh] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <span className="material-icons text-[#9ca3af] mb-4" style={{ fontSize: '56px' }}>notifications_none</span>
                  <p className="text-base font-medium text-[#202124] dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                    You're all caught up!
                  </p>
                  <p className="text-sm text-[#5f6368] dark:text-[#94a3b8] mt-2">
                    No new notifications at this time.
                  </p>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="p-4 bg-[#f8f9fa] dark:bg-[#374151]/50 rounded-xl border border-[#e0e0e0] dark:border-gray-600 hover:border-[#1a73e8] dark:hover:border-[#4285f4] transition-colors"
                    >
                      <div className="flex gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notification.type === 'action'
                            ? 'bg-[#e8f0fe] dark:bg-[#1a73e8]/20'
                            : 'bg-[#fef7e0] dark:bg-[#f9ab00]/20'
                        }`}>
                          <span
                            className={`material-icons ${
                              notification.type === 'action'
                                ? 'text-[#1a73e8]'
                                : 'text-[#f9ab00]'
                            }`}
                            style={{ fontSize: '24px' }}
                          >
                            {notification.icon}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-base font-medium text-[#202124] dark:text-white" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                              {notification.title}
                            </h3>
                            <button
                              onClick={() => dismissNotification(notification.id)}
                              className="p-1.5 rounded-full hover:bg-[#e5e7eb] dark:hover:bg-gray-600 text-[#9ca3af] hover:text-[#5f6368] dark:hover:text-gray-300 transition-colors flex-shrink-0"
                              title="Dismiss"
                            >
                              <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
                            </button>
                          </div>
                          <p className="text-sm text-[#5f6368] dark:text-[#94a3b8] mt-2 leading-relaxed">
                            {notification.message}
                          </p>
                          {notification.actionLabel && (
                            <button
                              onClick={() => {
                                navigate(notification.actionRoute);
                                setShowNotificationsModal(false);
                              }}
                              className="mt-3 text-sm font-medium text-[#1a73e8] hover:text-[#1557b0] dark:text-[#4285f4] dark:hover:text-[#669df6] transition-colors flex items-center gap-1"
                              style={{ fontFamily: "'Google Sans', sans-serif" }}
                            >
                              {notification.actionLabel}
                              <span className="material-icons" style={{ fontSize: '16px' }}>arrow_forward</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-6 py-4 bg-[#f8f9fa] dark:bg-[#111827] border-t border-[#e0e0e0] dark:border-gray-700">
                <button
                  onClick={() => {
                    notifications.forEach(n => dismissNotification(n.id));
                  }}
                  className="w-full text-center text-sm font-medium text-[#5f6368] dark:text-[#94a3b8] hover:text-[#1a73e8] dark:hover:text-[#4285f4] transition-colors"
                  style={{ fontFamily: "'Google Sans', sans-serif" }}
                >
                  Clear all notifications
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default AppShell;
